import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Box,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import DeleteIcon from "@mui/icons-material/Delete";
import { useTheme } from "../../theme/ThemeContext";
import AppButton from "./Button";
import { AppTypography } from "./Typography";
import { Input } from "./Input";
import Toaster from "../utils/Toaster";
import {
  useCreateSupportTicketMutation,
  useUploadFilesMutation,
} from "../../store/apiSlice";
import { useAuth } from "../../context/AuthContext";

const initialState = {
  topic: "",
  description: "",
  attachment: null,
};

const SupportPopup = ({ open, onClose }) => {
  const theme = useTheme();
  const { user } = useAuth();
  const [formValues, setFormValues] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [previewUrl, setPreviewUrl] = useState(null);
  const [createSupportTicket, { isLoading }] = useCreateSupportTicketMutation();
  const [uploadFiles, { isLoading: isUploading }] = useUploadFilesMutation();

  useEffect(() => {
    if (!open) {
      setFormValues(initialState);
      setErrors({});
      // Clean up preview URL when dialog closes
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
      }
    }
  }, [open]);

  // Clean up preview URL on unmount
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  // Show warning if user is not logged in
  useEffect(() => {
    if (open && !user?.id) {
      setErrors((prev) => ({
        ...prev,
        user_id: "Please log in to submit a support ticket",
      }));
    }
  }, [open, user]);

  const handleClose = () => {
    if (isLoading || isUploading) return;
    onClose?.();
  };

  const handleChange = (field) => (event) => {
    const value =
      field === "attachment"
        ? event.target.files?.[0] ?? null
        : event.target.value;
    
    if (field === "attachment") {
      // Clean up previous preview URL
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
      }
      
      // Create preview URL for images
      if (value && value.type.startsWith("image/")) {
        const url = URL.createObjectURL(value);
        setPreviewUrl(url);
      } else {
        setPreviewUrl(null);
      }
    }
    
    setFormValues((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const handleRemoveAttachment = () => {
    // Clean up preview URL
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    setFormValues((prev) => ({
      ...prev,
      attachment: null,
    }));
    // Reset file input
    const fileInput = document.getElementById("support-attachment");
    if (fileInput) {
      fileInput.value = "";
    }
  };

  const validate = () => {
    const validationErrors = {};
    if (!formValues.topic.trim()) {
      validationErrors.topic = "Topic is required";
    }
    if (!formValues.description.trim()) {
      validationErrors.description = "Description is required";
    }

    const userId = user?.id || "";
    if (!userId) {
      validationErrors.user_id = "Valid user is required. Please log in.";
    }

    return validationErrors;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    const userId = user?.id;
    if (!userId) {
      Toaster.show("Valid user is required. Please log in.", "error");
      return;
    }

    let attachmentUrls = [];
    if (formValues.attachment) {
      try {
        const fileFormData = new FormData();
        fileFormData.append("files", formValues.attachment);

        const uploadResult = await uploadFiles(fileFormData).unwrap();

        attachmentUrls =
          uploadResult?.result ||
          (Array.isArray(uploadResult) ? uploadResult : []);

        if (!attachmentUrls || attachmentUrls.length === 0) {
          Toaster.show(
            "Failed to upload attachment. Please try again.",
            "error"
          );
          return;
        }
      } catch (uploadError) {
        console.error("File upload error:", uploadError);
        Toaster.show(
          uploadError?.data?.message ||
            uploadError?.message ||
            "Failed to upload attachment. Please try again.",
          "error"
        );
        return;
      }
    }

    const payload = {
      topic: formValues.topic.trim(),
      description: formValues.description.trim(),
      user_id: userId,
      attachment_urls: attachmentUrls, // Array of URLs
    };

    try {
      const result = await createSupportTicket(payload).unwrap();

      const message =
        result?.result?.message ||
        result?.message ||
        "Support request submitted successfully";
      Toaster.show(message, "success");

      setFormValues(initialState);
      onClose?.();
    } catch (error) {
      Toaster.show(
        error?.data?.message ||
          error?.message ||
          "Failed to submit support request",
        "error"
      );
    }
  };

  const attachmentName = formValues.attachment?.name;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: theme?.card_bg_color || theme?.background?.card || "#fff",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          py: 2.5,
          borderBottom: `1px solid ${theme?.divider || "rgba(0, 0, 0, 0.12)"}`,
        }}
      >
        <AppTypography
          variant="h4"
          fontSize="1.5rem"
          fontWeight={600}
          sx={{ color: theme?.text_color }}
        >
          Customer Support
        </AppTypography>
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{
            color: theme?.text_color || "rgba(0, 0, 0, 0.54)",
            "&:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.04)",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          display: "flex",
          flexDirection: "column",
        }}
      >
        <DialogContent
          sx={{
            px: 3,
            py: 3,
            display: "flex",
            flexDirection: "column",
            gap: 3,
          }}
        >
          <Input
            label="Topic"
            value={formValues.topic}
            onChange={handleChange("topic")}
            error={Boolean(errors.topic)}
            helperText={errors.topic}
            placeholder="Briefly describe your issue"
            required
          />
          <Input
            label="Description"
            value={formValues.description}
            onChange={handleChange("description")}
            error={Boolean(errors.description)}
            helperText={errors.description}
            placeholder="Share more details with us"
            multiline
            minRows={5}
            required
          />

          {/* Email validation error display */}
          {errors.user_id && (
            <Box
              sx={{
                p: 1.5,
                borderRadius: 1,
                backgroundColor: "rgba(211, 47, 47, 0.1)",
                border: "1px solid rgba(211, 47, 47, 0.3)",
              }}
            >
              <AppTypography
                variant="body2"
                sx={{
                  color: "#d32f2f",
                  fontSize: "0.875rem",
                }}
              >
                {errors.user_id}
              </AppTypography>
            </Box>
          )}

          <Box>
            <AppTypography
              variant="body2"
              fontWeight={500}
              sx={{
                mb: 1.5,
                color: theme?.text_color || "rgba(0, 0, 0, 0.87)",
              }}
            >
              Attachment (optional)
            </AppTypography>
            <input
              id="support-attachment"
              type="file"
              accept=".png,.jpg,.jpeg,.pdf,.doc,.docx,.txt"
              style={{ display: "none" }}
              onChange={handleChange("attachment")}
            />
            <label htmlFor="support-attachment">
              <AppButton
                component="span"
                variant="outlined"
                size="medium"
                startIcon={<AttachFileIcon />}
                sx={{
                  borderColor: theme?.divider || "rgba(0, 0, 0, 0.23)",
                  color: theme?.text_color || "rgba(0, 0, 0, 0.87)",
                  backgroundColor: "transparent",
                  "&:hover": {
                    borderColor: theme?.app_button_color,
                    backgroundColor: `${theme?.app_button_color}08`,
                  },
                }}
              >
                {attachmentName ? "Change file" : "Choose file"}
              </AppButton>
            </label>
            <AppTypography
              variant="caption"
              sx={{
                mt: 0.5,
                ml: 1,
                color: theme?.text_color 
                  ? `${theme.text_color}80` 
                  : "rgba(0, 0, 0, 0.6)",
                fontSize: "0.75rem",
              }}
            >
              Max file size: 5MB
            </AppTypography>
            {attachmentName && (
              <Box
                sx={{
                  mt: 1.5,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                }}
              >
                {/* Image Preview */}
                {previewUrl && (
                  <Box
                    sx={{
                      position: "relative",
                      width: "100%",
                      maxWidth: { xs: "100%", sm: 450, md: 500 },
                      margin: "0 auto",
                      borderRadius: 2,
                      overflow: "hidden",
                      border: `1px solid ${theme?.divider || "rgba(0, 0, 0, 0.12)"}`,
                      backgroundColor: theme?.divider
                        ? `${theme.divider}10`
                        : "rgba(0, 0, 0, 0.02)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: { xs: 1, sm: 1.5 },
                      minHeight: { xs: 150, sm: 200 },
                    }}
                  >
                    <Box
                      component="img"
                      src={previewUrl}
                      alt="Preview"
                      sx={{
                        maxWidth: "100%",
                        maxHeight: { xs: 250, sm: 320, md: 380 },
                        width: "auto",
                        height: "auto",
                        objectFit: "contain",
                        objectPosition: "center",
                        display: "block",
                        borderRadius: 1,
                      }}
                    />
                    <IconButton
                      onClick={handleRemoveAttachment}
                      sx={{
                        position: "absolute",
                        top: { xs: 4, sm: 8 },
                        right: { xs: 4, sm: 8 },
                        backgroundColor: "rgba(0, 0, 0, 0.6)",
                        color: "white",
                        "&:hover": {
                          backgroundColor: "rgba(0, 0, 0, 0.8)",
                        },
                        width: { xs: 28, sm: 32 },
                        height: { xs: 28, sm: 32 },
                      }}
                      size="small"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}
                
                {/* File Name Display (for non-image files) */}
                {!previewUrl && (
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 1,
                      backgroundColor: theme?.divider
                        ? `${theme.divider}20`
                        : "rgba(0, 0, 0, 0.04)",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <AppTypography
                      variant="body2"
                      sx={{
                        color: theme?.text_color || "rgba(0, 0, 0, 0.87)",
                        flex: 1,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {attachmentName}
                    </AppTypography>
                    <IconButton
                      onClick={handleRemoveAttachment}
                      size="small"
                      sx={{
                        color: theme?.text_color || "rgba(0, 0, 0, 0.54)",
                        "&:hover": {
                          backgroundColor: "rgba(0, 0, 0, 0.04)",
                        },
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        <Divider />
        <DialogActions
          sx={{
            px: 3,
            py: 2.5,
            gap: 1.5,
            justifyContent: "flex-end",
          }}
        >
          <AppButton
            type="button"
            variant="outlined"
            onClick={handleClose}
            disabled={isLoading || isUploading}
            sx={{
              minWidth: 100,
              borderColor: theme?.divider || "rgba(0, 0, 0, 0.23)",
              color: theme?.text_color || "rgba(0, 0, 0, 0.87)",
              backgroundColor: "transparent",
              "&:hover": {
                borderColor: theme?.divider || "rgba(0, 0, 0, 0.23)",
                backgroundColor: theme?.divider
                  ? `${theme.divider}10`
                  : "rgba(0, 0, 0, 0.04)",
              },
              "&:disabled": {
                borderColor: theme?.divider || "rgba(0, 0, 0, 0.12)",
                color: "rgba(0, 0, 0, 0.26)",
              },
            }}
          >
            Cancel
          </AppButton>
          <AppButton
            type="submit"
            variant="contained"
            disabled={isLoading || isUploading}
            sx={{
              minWidth: 120,
              backgroundColor: theme?.app_button_color,
              color: theme?.app_button_text_color || "#fff",
              "&:hover": {
                backgroundColor: theme?.app_hover_button_color,
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
              },
              "&:disabled": {
                backgroundColor: "rgba(0, 0, 0, 0.12)",
                color: "rgba(0, 0, 0, 0.26)",
              },
            }}
          >
            {isLoading || isUploading
              ? isUploading
                ? "Uploading..."
                : "Submitting..."
              : "Submit"}
          </AppButton>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default SupportPopup;
